// The circle: seats around an ellipse, the countdown in the middle, and the vote clock's hand over
// the top. Both moving parts are drawn from server timestamps and decide nothing; the server fires
// the real expiry and vote lock, so a browser with a wrong clock only ever looks wrong.

import { useEffect, useMemo, useState } from "react";
import { isSeatVoteLocked, totalSweepMs } from "@cc/shared";
import type { GameView, Nomination } from "@cc/shared";
import { serverNow } from "../../state/game.js";
import { cn, formatDuration } from "../../lib/ui.js";
import { Seat } from "./Seat.js";
import { TeamCounts } from "./TeamCounts.js";

/** Seat positions on an ellipse, starting at the top and going clockwise. */
function seatPosition(index: number, count: number): { left: string; top: string } {
  const angle = (index / Math.max(count, 1)) * Math.PI * 2 - Math.PI / 2;
  return {
    left: `${50 + Math.cos(angle) * 40}%`,
    top: `${50 + Math.sin(angle) * 40}%`,
  };
}

function useTick(active: boolean, ms = 250): number {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setN((n) => n + 1), ms);
    return () => clearInterval(id);
  }, [active, ms]);
  return 0;
}

function VoteHand({ nomination, seatCount }: { nomination: Nomination; seatCount: number }) {
  const total = totalSweepMs(nomination.clockSeatIds.length || seatCount, nomination.msPerSeat);

  // Captured once when the vote starts: rewriting animation-delay on a running CSS animation re-
  // times it, which made the hand outrun the server's clock.
  const initialDelayMs = useMemo(
    () => (nomination.clockStartedAt === null ? 0 : Math.max(0, serverNow() - nomination.clockStartedAt)),
    [nomination.id, nomination.clockStartedAt],
  );

  if (nomination.clockStartedAt === null || nomination.status !== "voting") return null;
  const elapsed = serverNow() - nomination.clockStartedAt;
  if (elapsed > total) return null;

  // The hand starts at the nominee's seat. It hangs downward at rotate(0) while seat 0 is at the
  // top, hence the 180° offset.
  const nomineeIndex = Math.max(0, nomination.clockSeatIds.indexOf(nomination.nomineeSeatId));
  const startAngle = 180 + (nomineeIndex / Math.max(nomination.clockSeatIds.length, 1)) * 360;

  // Both wrappers are full-size boxes rotated about the circle's centre, so the hand's length is a
  // percentage of the circle (40%, where the seats sit).
  return (
    <div className="pointer-events-none absolute inset-0" style={{ transform: `rotate(${startAngle}deg)` }}>
      <div
        className="absolute inset-0"
        style={{
          animation: `cc-sweep ${total}ms linear 1 forwards`,
          animationDelay: `-${initialDelayMs}ms`,
        }}
      >
        <div className="absolute left-1/2 top-1/2 h-[40%] w-0.5 origin-top -translate-x-1/2 bg-gradient-to-b from-brass via-brass/70 to-transparent shadow-[0_0_8px_var(--color-brass)]" />
      </div>
    </div>
  );
}

function Countdown({ endsAt, label }: { endsAt: number; label: string }) {
  useTick(true, 250);
  const remaining = endsAt - serverNow();
  return (
    <div className="pointer-events-none flex flex-col items-center">
      <span className="text-sm uppercase tracking-widest text-parchment-dim">{label}</span>
      <span className={cn("text-4xl font-semibold tabular-nums", remaining <= 30_000 && "text-brass")}>
        {formatDuration(remaining)}
      </span>
    </div>
  );
}

export function Circle({
  view,
  onSeatClick,
  onGuessClick,
  onMoveSeat,
  onRemoveSeat,
}: {
  view: GameView;
  onSeatClick: (seatId: string) => void;
  onGuessClick: (seatId: string) => void;
  /** Storyteller only; omitted for players, which is what hides the on-board seat tools. */
  onMoveSeat?: (seatId: string, direction: -1 | 1) => void;
  onRemoveSeat?: (seatId: string) => void;
}) {
  useTick(view.openNominationId !== null, 300);

  const openNomination = view.nominations.find((n) => n.id === view.openNominationId) ?? null;
  const seatCount = view.seats.length;

  function lockedFor(seatId: string): boolean {
    if (!openNomination || openNomination.clockStartedAt === null) return false;
    const ids = openNomination.clockSeatIds;
    const nomineeIndex = ids.indexOf(openNomination.nomineeSeatId);
    const seatIndex = ids.indexOf(seatId);
    if (nomineeIndex === -1 || seatIndex === -1) return true;
    return isSeatVoteLocked(
      ids.length,
      nomineeIndex,
      seatIndex,
      openNomination.msPerSeat,
      serverNow() - openNomination.clockStartedAt,
    );
  }

  return (
    // Square, and capped by the height left after the header and control bar, so the circle shrinks
    // as the controls grow.
    <div className="relative aspect-square max-h-full w-full max-w-[64rem]">
      <div className="absolute inset-[12%] rounded-full border border-ink-line/40" />

      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
        {view.countdown ? (
          <Countdown endsAt={view.countdown.endsAt} label={view.countdown.label} />
        ) : seatCount === 0 ? (
          <p className="text-sm text-parchment-dim">Nobody has joined yet — share the code.</p>
        ) : (
          <div className="text-sm uppercase tracking-widest text-parchment-dim/70">
            {view.phase.kind === "setup"
              ? "Setup"
              : `${view.phase.kind === "night" ? "Night" : "Day"} ${view.phase.number}`}
          </div>
        )}
        {seatCount > 0 && <TeamCounts view={view} />}
      </div>

      {openNomination && <VoteHand nomination={openNomination} seatCount={seatCount} />}

      {view.seats.map((seat, index) => {
        const pos = seatPosition(index, seatCount);
        return (
          <div
            key={seat.id}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: pos.left, top: pos.top }}
          >
            <Seat
              seat={seat}
              view={view}
              isMe={seat.id === view.you.seatId}
              handRaised={Boolean(openNomination?.hands[seat.id])}
              voteLocked={lockedFor(seat.id)}
              hasVoteIn={openNomination?.status === "voting" && Boolean(openNomination.hands[seat.id])}
              isNominee={openNomination?.nomineeSeatId === seat.id}
              onClick={() => onSeatClick(seat.id)}
              onGuess={view.you.isStoryteller ? undefined : () => onGuessClick(seat.id)}
              {...(onMoveSeat ? { onMoveSeat: (d: -1 | 1) => onMoveSeat(seat.id, d) } : {})}
              {...(onRemoveSeat ? { onRemove: () => onRemoveSeat(seat.id) } : {})}
            />
          </div>
        );
      })}

    </div>
  );
}
